import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { APP_API_ENDPOINTS } from '../constants/app-api.const';
import { APP_ROUTES } from '../constants/app-routes.const';
import { AuthService } from '../services/auth.service';
import { AuthStore } from '../stores/auth.store';

/**
 * 🚨 Enterprise Error Interceptor (Bắt lỗi toàn cục API)
 * - Tự động phát hiện lỗi Business (status: false).
 * - Tự động phát hiện lỗi HTTP (401, 403, 500...).
 * - Tự động Refresh Token khi gặp lỗi 401 (Unauthorized).
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);

  /**
   * Xử lý lỗi 401: Refresh Token Queueing Logic
   */
  const handle401Error = (request: HttpRequest<unknown>) => {
    if (authService.isRefreshing) {
      // Đã có request khác đang refresh, ta chỉ cần đợi tín hiệu từ Subject
      return authService.refreshTokenSubject.pipe(
        // ✅ Chờ tín hiệu khác null ban đầu (khi refreshTokenSubject.next(null) được gọi)
        // Nhưng PHẢI cho qua cả giá trị rỗng '' để unlock hàng đợi khi refresh thất bại
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          // Token rỗng '' = refresh thất bại → trả lỗi cho request trong hàng đợi
          if (!token) {
            return throwError(() => new Error('Refresh token failed'));
          }
          return next(
            request.clone({
              headers: request.headers
                .set('Authorization', `Bearer ${token}`)
                .set('X-Retry-401', 'true'),
            }),
          );
        }),
      );
    }

    // Đây là request đầu tiên phát hiện 401 -> Kích hoạt Refresh Token
    authService.isRefreshing = true;
    authService.refreshTokenSubject.next(null);

    return authService.refreshTokenApi().pipe(
      catchError((err) => {
        // ✅ Gửi chuỗi rỗng '' thay vì null để unlock hàng đợi
        authService.refreshTokenSubject.next('');
        authService.forceLogout(); // Dọn dẹp local mà KHÔNG gọi API logout
        return throwError(() => err);
      }),
      switchMap((response) => {
        const newToken = response.data;
        if (newToken) {
          authStore.setToken(newToken);
          authService.refreshTokenSubject.next(newToken); // Thông báo cho hàng đợi

          // Chạy lại request bị lỗi ban đầu với Token mới + đánh dấu đã retry để tránh loop
          return next(
            request.clone({
              headers: request.headers
                .set('Authorization', `Bearer ${newToken}`)
                .set('X-Retry-401', 'true'),
            }),
          );
        }
        authService.refreshTokenSubject.next('');
        authService.forceLogout();
        return throwError(() => new Error('Refresh token failed'));
      }),
      finalize(() => (authService.isRefreshing = false)),
    );
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!(error.error instanceof ErrorEvent)) {
        // Các API Auth không cần trigger Refresh Token để tránh vòng lặp
        const noRefreshEndpoints = [
          APP_API_ENDPOINTS.AUTH.LOGIN,
          APP_API_ENDPOINTS.AUTH.REFRESH_TOKEN,
          APP_API_ENDPOINTS.AUTH.GET_OTP,
          APP_API_ENDPOINTS.AUTH.VERIFY_OTP,
          APP_API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        ];
        const isNoRefreshApi = noRefreshEndpoints.some((endpoint) =>
          req.url.includes(endpoint),
        );

        // Kiểm tra nếu request này đã được retry nhưng vẫn 401 -> Dừng lại để tránh loop
        const isRetried = req.headers.has('X-Retry-401');

        switch (error.status) {
          case 401:
            // ✅ Chỉ Refresh nếu không thuộc danh sách bỏ qua và chưa retry lần nào
            if (!isNoRefreshApi && !isRetried) return handle401Error(req);

            // Nếu API Refresh Token lỗi 401 hoặc request đã retry vẫn 401 -> Logout
            if (
              req.url.includes(APP_API_ENDPOINTS.AUTH.REFRESH_TOKEN) ||
              isRetried
            ) {
              authService.forceLogout();
            }
            // ✅ Không hiển thị toast cho 401 → return sớm
            return throwError(() => error);
          case 403:
            router.navigate(['/' + APP_ROUTES.FORBIDDEN]).then();
            break;
        }
      }

      // Ném lỗi tiếp để BaseComponent xử lý Toast (single source of truth)
      return throwError(() => error);
    }),
  );
};
